import { useParams, useNavigate } from 'react-router-dom'
import { Card, ErrorState, Loading, Metric, MetricRow, SectionHeader, StatusPill, Table, fmtMinutes, fmtPct } from '../components.jsx'
import { useApi } from '../hooks.js'

/**
 * One crane, end to end: identity, statutory position, reliability, and its maintenance history.
 *
 * This is the drill-down every dashboard number leads to. The point of a console over a spreadsheet is that
 * "CRANE-03 has 40% of fleet downtime" is a link, not a fact you then have to go and investigate by hand.
 *
 * Statutory state comes from the server as a StatusMap key (`ComplianceSummary.state`), so the console renders
 * VALID / DUE_SOON / OVERDUE without re-deriving the rule — the derivation lives with the lockout logic that
 * enforces it, and a second implementation here would eventually disagree about whether a crane may be operated.
 */
export default function CraneDetail() {
  const { id } = useParams()
  const nav = useNavigate()

  const detail = useApi(`/assets/${id}/detail`)
  const rel = useApi('/rcm/asset-reliability')
  const logs = useApi(`/maintenance-logs?assetId=${id}`)

  const d = detail.data
  const a = d?.asset
  const mine = (rel.data || []).find((r) => r.assetId === id)
  const attrs = parseAttrs(a?.attributes)

  return (
    <>
      <header className="page-head">
        <div>
          <button className="btn" onClick={() => nav(-1)} style={{ marginBottom: 'var(--sm)' }}>← Back</button>
          <h1>{a ? `${a.assetCode} · ${a.name}` : 'Crane'}</h1>
          {a && <p className="muted">{[a.department, a.area, a.bay, a.locationDesc].filter(Boolean).join(' · ')}</p>}
        </div>
        {a && (
          <div className="page-controls">
            <StatusPill status={a.status} />
            {a.doNotOperate && <StatusPill status="LOCKED_OUT" title={a.doNotOperateReason || undefined} />}
          </div>
        )}
      </header>

      {detail.loading && <Loading rows={6} />}
      {detail.error && <ErrorState error={detail.error} onRetry={detail.reload} />}

      {d && (
        <>
          {/* The safety banner comes first, because it is the one thing that changes what may be done today. */}
          {a.doNotOperate && (
            <div className="banner banner-danger" style={{ fontWeight: 600 }}>
              <span>DO NOT OPERATE — {a.doNotOperateReason || 'locked out pending review'}</span>
            </div>
          )}

          <MetricRow>
            <Metric label="Availability (90d)" value={fmtPct(mine?.availabilityPct)} unit="%" kind={availKind(mine?.availabilityPct)} />
            <Metric label="MTBF" value={fmtMinutes(mine?.mtbfMinutes)} hint={mine?.failures ? `${mine.failures} failures` : 'No failures'} />
            <Metric label="MTTR" value={fmtMinutes(mine?.mttrMinutes)} />
            <Metric label="Downtime (90d)" value={fmtMinutes(mine?.downtimeMinutes)} />
            <Metric
              label="Repeat failures"
              value={mine?.repeatFailures ?? 0}
              kind={mine?.repeatFailures ? 'warning' : undefined}
              hint={mine?.repeatFailures ? 'Root cause not eliminated' : undefined}
            />
          </MetricRow>

          <div className="grid-2" style={{ marginTop: 'var(--lg)' }}>
            <Card title="Statutory compliance" subtitle="Derived from examination records, never stored twice">
              {d.compliance?.length ? (
                <Table
                  keyOf={(c) => c.complianceDefCode}
                  columns={[
                    { key: 'code', header: 'Obligation', render: (c) => c.complianceDefCode.replace(/_/g, ' ') },
                    { key: 'state', header: 'State', render: (c) => <StatusPill status={c.state} /> },
                    { key: 'examined', header: 'Examined', render: (c) => c.examinedAt || <span className="muted">—</span> },
                    { key: 'until', header: 'Valid until', render: (c) => c.validUntil || <span className="muted">—</span> },
                    { key: 'result', header: 'Result', render: (c) => c.result || <span className="muted">—</span> },
                  ]}
                  rows={d.compliance}
                />
              ) : (
                <p className="muted">
                  No statutory examinations on record for this crane. For a crane in service that is itself a
                  finding — a load test is mandatory.
                </p>
              )}
            </Card>

            <Card title="Nameplate">
              <Table
                keyOf={(r) => r.k}
                columns={[
                  { key: 'k', header: 'Field' },
                  { key: 'v', header: 'Value' },
                ]}
                rows={[
                  { k: 'Crane no', v: a.assetCode },
                  { k: 'Type', v: a.equipmentTypeCode },
                  { k: 'Capacity', v: attrs['base.capacity_tonnes'] ? `${attrs['base.capacity_tonnes']} t` : '—' },
                  { k: 'Safe load limit', v: attrs['base.swl_tonnes'] ? `${attrs['base.swl_tonnes']} t` : '—' },
                  { k: 'Span', v: attrs['base.span_m'] ? `${attrs['base.span_m']} m` : '—' },
                  { k: 'Make', v: attrs['base.make'] || '—' },
                  { k: 'Model', v: attrs['base.model'] || '—' },
                  { k: 'Serial no', v: attrs['base.serial_no'] || '—' },
                  { k: 'Installed', v: a.installDate || '—' },
                  { k: 'Warranty until', v: a.warrantyUntil || '—' },
                ]}
              />
            </Card>
          </div>

          <SectionHeader hint="Open faults on this crane right now">Open complaints</SectionHeader>
          <Card>
            <Table
              keyOf={(c) => c.id}
              columns={[
                { key: 'priority', header: 'Priority', render: (c) => <StatusPill status={c.priority} /> },
                { key: 'status', header: 'Status', render: (c) => <StatusPill status={c.status} /> },
                { key: 'discipline', header: 'Discipline', render: (c) => titleCase(c.discipline) },
                { key: 'what', header: 'Reported', render: (c) => c.descriptionText || <span className="muted">No description</span> },
              ]}
              rows={d.openComplaints || []}
              empty={<p className="muted">Nothing open on this crane.</p>}
            />
          </Card>

          <SectionHeader hint="Planned and corrective work recorded against this crane">
            Maintenance history
          </SectionHeader>
          <Card>
            {logs.loading && <Loading rows={4} />}
            {logs.error && <ErrorState error={logs.error} onRetry={logs.reload} />}
            {logs.data && (
              <Table
                keyOf={(l) => l.id}
                columns={[
                  { key: 'when', header: 'Performed', render: (l) => fmtDate(l.performedAt) },
                  { key: 'type', header: 'Activity', render: (l) => titleCase(l.activityType) },
                  { key: 'discipline', header: 'Discipline', render: (l) => titleCase(l.discipline) },
                  { key: 'what', header: 'Description', render: (l) => <span className="cell-clamp" title={l.description || ''}>{l.description || <span className="muted">—</span>}</span> },
                  { key: 'downtime', header: 'Downtime', numeric: true, render: (l) => (l.downtimeMinutes ? fmtMinutes(l.downtimeMinutes) : <span className="muted">—</span>) },
                  { key: 'parts', header: 'Parts', render: (l) => l.partsUsed || <span className="muted">—</span> },
                ]}
                rows={logs.data}
                empty={<p className="muted">No maintenance recorded against this crane yet.</p>}
              />
            )}
          </Card>
        </>
      )}
    </>
  )
}

/**
 * Attributes arrive as a JSON string of namespaced groups (`base`, `electrical`, `mechanical`, `safety`,
 * `hoists`) — CLAUDE.md rule 11 keeps crane-specific fields out of core columns. Flattened to dotted keys so a
 * lookup does not need to know which namespace a field lives in.
 */
function parseAttrs(json) {
  if (!json) return {}
  try {
    const obj = JSON.parse(json)
    const out = {}
    Object.entries(obj).forEach(([group, vals]) => {
      if (vals && typeof vals === 'object' && !Array.isArray(vals)) {
        Object.entries(vals).forEach(([k, v]) => { out[`${group}.${k}`] = v })
      } else {
        out[group] = vals
      }
    })
    return out
  } catch {
    return {}
  }
}

const titleCase = (s) => (s ? s.split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ') : '—')
const availKind = (p) => (p === undefined || p === null ? undefined : p >= 95 ? 'success' : p >= 85 ? 'warning' : 'danger')

function fmtDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}
